// Optimizasyon (C13–C15, AI_OPTIMIZATION bayrağı) ve rota versiyonları (C16/C17, çekirdek planlama): Zod girdileri ve
// yanıt tipleri (API ve konsol aynı tipi kullanır). Senaryo ölçütleri teklif ölçütleriyle (ProposalMetrics) aynı anahtarları
// taşır; senaryo rota belgesi `ProposalRoutesDoc` biçimindedir — teklif bir senaryodan doğrudan üretilebilir.
// KVKK: optimizasyon yanıtlarında yolcu adı, telefonu veya kimliği yoktur; yalnız sayılar ve durak/rota bilgisi.
import { z } from 'zod';
import type { Direction } from '../enums';
import type { Paged } from './common';
import type { CompareRoute, ProposalMetricDelta, ProposedRouteChange } from './commercial';
import type { RouteVersionDetail, RouteVersionItem } from './planning';

const Id = z.string().min(1).max(64);

// ───────── stratejiler ve durumlar ─────────

export const OPTIMIZATION_STRATEGIES = ['MIN_VEHICLES', 'MIN_KM', 'BALANCED'] as const;
export type OptimizationStrategy = (typeof OPTIMIZATION_STRATEGIES)[number];

/** Senaryo harfi stratejiye sabit bağlıdır: A en az araç, B en kısa yol, C dengeli. */
export const STRATEGY_LABEL: Record<OptimizationStrategy, 'A' | 'B' | 'C'> = {
  MIN_VEHICLES: 'A',
  MIN_KM: 'B',
  BALANCED: 'C',
};
export const STRATEGY_TITLES: Record<OptimizationStrategy, string> = {
  MIN_VEHICLES: 'En az araç',
  MIN_KM: 'En kısa yol',
  BALANCED: 'Dengeli',
};
export const STRATEGY_DESCRIPTIONS: Record<OptimizationStrategy, string> = {
  MIN_VEHICLES: 'Yakın durakları birleştirir, araçları kapasiteye kadar doldurur; araç sayısını önce düşürür.',
  MIN_KM: 'Toplam yolu en aza indirir; gerekirse araç sayısı aynı kalır.',
  BALANCED: 'Yürüme ve yolculuk süresini korur; yalnız belirgin kazançta durak ve hat değiştirir.',
};

export const OPTIMIZATION_RUN_STATUSES = ['QUEUED', 'RUNNING', 'DONE', 'FAILED'] as const;
export type OptimizationRunStatus = (typeof OPTIMIZATION_RUN_STATUSES)[number];
export const OPTIMIZATION_RUN_STATUS_LABELS: Record<OptimizationRunStatus, string> = {
  QUEUED: 'Sırada',
  RUNNING: 'Hesaplanıyor',
  DONE: 'Tamamlandı',
  FAILED: 'Tamamlanamadı',
};

/** İlerleme aşamaları (yüzde eşiği → metin). */
export const OPTIMIZATION_STAGES: { from: number; label: string }[] = [
  { from: 0, label: 'Sırada' },
  { from: 5, label: 'Yolcu ve durak verisi okunuyor' },
  { from: 15, label: 'Mevcut düzen ölçülüyor' },
  { from: 25, label: 'Yakın duraklar birleştiriliyor' },
  { from: 35, label: 'Senaryo A hesaplanıyor' },
  { from: 55, label: 'Senaryo B hesaplanıyor' },
  { from: 75, label: 'Senaryo C hesaplanıyor' },
  { from: 92, label: 'Güzergâhlar çiziliyor' },
  { from: 100, label: 'Tamamlandı' },
];

export function optimizationStage(progress: number): string {
  let label = OPTIMIZATION_STAGES[0]!.label;
  for (const s of OPTIMIZATION_STAGES) if (progress >= s.from) label = s.label;
  return label;
}

// ───────── girdiler ─────────

export const OPTIMIZATION_DEFAULTS = { maxWalkM: 400, maxRideMin: 75, capacityBufferPct: 0, maxStops: 12 } as const;

export const OptimizationConstraintsInput = z.object({
  /** yolcunun evinden durağa en fazla yürüme (m); daha uzağa taşınmaz (bugün daha uzaktaysa bugünkü korunur) */
  maxWalkM: z
    .number()
    .int()
    .min(100, 'Yürüme mesafesi en az 100 m olmalı.')
    .max(1500, 'Yürüme mesafesi en fazla 1.500 m olabilir.')
    .default(OPTIMIZATION_DEFAULTS.maxWalkM),
  /** binişten varışa en uzun yolculuk (dk); bugün daha uzun süren yolcunun süresi uzatılmaz */
  maxRideMin: z
    .number()
    .int()
    .min(20, 'Yolculuk süresi en az 20 dakika olmalı.')
    .max(180, 'Yolculuk süresi en fazla 180 dakika olabilir.')
    .default(OPTIMIZATION_DEFAULTS.maxRideMin),
  /** araç kapasitesinin boş bırakılacak yüzdesi (yedek koltuk) */
  capacityBufferPct: z
    .number()
    .int()
    .min(0, 'Kapasite payı 0 ile 30 arasında olmalı.')
    .max(30, 'Kapasite payı 0 ile 30 arasında olmalı.')
    .default(OPTIMIZATION_DEFAULTS.capacityBufferPct),
  maxStops: z
    .number()
    .int()
    .min(3, 'Bir hatta en az 3 durak olmalı.')
    .max(30, 'Bir hatta en fazla 30 durak olabilir.')
    .default(OPTIMIZATION_DEFAULTS.maxStops),
});
export type OptimizationConstraintsInput = z.input<typeof OptimizationConstraintsInput>;
export type OptimizationConstraints = z.output<typeof OptimizationConstraintsInput>;

export const OptimizationRunInput = z.object({
  institutionIds: z.array(Id).min(1, 'En az bir kurum seçin.').max(10, 'En fazla 10 kurum seçebilirsiniz.'),
  /** boşsa kurumların tüm aktif rotaları */
  routeIds: z.array(Id).max(80).optional(),
  direction: z.enum(['OUTBOUND', 'INBOUND']).default('OUTBOUND'),
  constraints: OptimizationConstraintsInput.prefault({}),
});
export type OptimizationRunInput = z.input<typeof OptimizationRunInput>;

export const OptimizationRunListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(10),
});
export type OptimizationRunListQuery = z.input<typeof OptimizationRunListQuery>;

export const ScenarioApproveInput = z.object({
  note: z.string().trim().max(200, 'Not en fazla 200 karakter olabilir.').optional(),
});
export type ScenarioApproveInput = z.infer<typeof ScenarioApproveInput>;

export const ScenarioPublishInput = z.object({ notify: z.boolean().default(true) });
export type ScenarioPublishInput = z.input<typeof ScenarioPublishInput>;

// ───────── yanıtlar ─────────

/**
 * Ölçütler (mevcut ve senaryo aynı yöntemle). İlk altı anahtar `ProposalMetrics` ile aynıdır.
 * `km`: günlük toplam (rota uzunluğu × tarifedeki yön sayısı). `avgMinutes`: kapsam yönünde binişten varışa ortalama.
 * `monthlyCost`: sözleşme fiyatlamasıyla aylık tahmini hakediş (kuruş). `walkM`: evden durağa ortalama yürüme tahmini.
 */
export interface OptimizationMetrics {
  vehicles: number;
  km: number;
  avgMinutes: number;
  monthlyCost: number;
  walkM: number;
  occupancy: number;
  passengers: number;
  routes: number;
  stops: number;
  walkMaxM: number;
  rideMaxMin: number;
  /** yolculuğu üst sınırı aşan yolcu sayısı (bugün zaten aşanlar dahil) */
  rideOverLimit: number;
  /** hiçbir araca sığmayan yolcu */
  unassigned: number;
}

export interface OptimizationInstitutionOption {
  id: string;
  name: string;
  routes: number;
  passengers: number;
}

export interface OptimizationRouteOption {
  id: string;
  code: string;
  name: string;
  institutionId: string;
  passengers: number;
  vehicle: { plate: string; capacity: number } | null;
}

export interface OptimizationOptions {
  institutions: OptimizationInstitutionOption[];
  routes: OptimizationRouteOption[];
  defaults: OptimizationConstraints;
  /** etkin çözücü: yerel sezgisel ya da VROOM sunucusu */
  optimizer: 'local' | 'vroom';
  /** firmada yürüyen çalıştırma varsa yenisi başlatılamaz */
  running: string | null;
}

export interface OptimizationScopeView {
  institutions: { id: string; name: string }[];
  routeCodes: string[];
  /** yalnız seçilen rotalar mı (yoksa kurumun tüm aktif rotaları) */
  subset: boolean;
  direction: Direction;
}

export interface ScenarioSummary {
  label: string;
  strategy: OptimizationStrategy;
  vehiclesDelta: number;
  kmDelta: number;
  monthlyCostDelta: number;
}

export interface OptimizationRunRow {
  id: string;
  status: OptimizationRunStatus;
  progress: number;
  stage: string;
  scope: OptimizationScopeView;
  constraints: OptimizationConstraints;
  baseline: OptimizationMetrics | null;
  /** önerilen senaryonun mevcut düzene göre farkı */
  recommended: ScenarioSummary | null;
  approved: { label: string; at: string; by: string | null } | null;
  createdBy: string | null;
  createdAt: string;
  finishedAt: string | null;
  error: string | null;
}

export interface OptimizationRunList extends Paged<OptimizationRunRow> {
  running: number;
}

export interface ScenarioRouteRow {
  code: string;
  name: string;
  change: ProposedRouteChange;
  /** birleştirilen / yerine geçilen mevcut rotalar */
  replaces: string[];
  stops: number;
  passengers: number;
  vehicle: { plate: string; capacity: number } | null;
  /** mevcut hattın aracı bu yüke yetmiyor; tarifeden daha büyük araç atanmalı */
  vehicleChange: boolean;
  occupancyPct: number | null;
  distanceM: number;
  durationS: number;
  /** en uzun yolculuk (dk) */
  maxRideMin: number;
  /** varış saatini koruyan önerilen kalkış (kapsam yönü) */
  departure: string | null;
  arrival: string | null;
  current: { distanceM: number; durationS: number; passengers: number; stops: number } | null;
}

/** Yolcu etkisi — yalnız sayılar. */
export interface ScenarioImpact {
  passengers: number;
  sameStop: number;
  stopChanged: number;
  /** hattı değişen (başka araca geçen) */
  routeChanged: number;
  walk: { less: number; same: number; upTo100: number; upTo250: number; more: number };
  ride: { shorter: number; similar: number; longer: number };
  avgWalkDeltaM: number;
  maxWalkDeltaM: number;
  thresholds: { sameWalkM: number; similarRideMin: number };
}

export interface ScenarioDraft {
  routeId: string;
  code: string;
  versionId: string;
  version: number;
  /** güncel durum: taslak, yayında, arşiv ya da taslak silindi */
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'DELETED';
  incoming: number;
}

export interface OptimizationScenarioItem {
  id: string;
  label: string;
  strategy: OptimizationStrategy;
  title: string;
  metrics: OptimizationMetrics;
  deltas: ProposalMetricDelta[];
  routes: ScenarioRouteRow[];
  removed: string[];
  impact: ScenarioImpact;
  /** harita için önerilen hatlar (yolcu sayılarıyla) */
  geometry: CompareRoute[];
  recommended: boolean;
  /** mevcut düzenle aynı sonuç (iyileştirme bulunamadı) */
  unchanged: boolean;
  /** başka bir senaryoyla aynı sonuç (ör. B, A ile aynı) */
  sameAs: string | null;
  warnings: string[];
  approvedAt: string | null;
  approvedBy: string | null;
}

export interface OptimizationRunDetail extends OptimizationRunRow {
  optimizer: string;
  /** haritada mevcut hatlar (kapsamdaki yayındaki versiyonlar) */
  current: CompareRoute[];
  scenarios: OptimizationScenarioItem[];
  recommendedLabel: string | null;
}

export interface RemovedRouteState {
  id: string;
  code: string;
  name: string;
  /** hâlâ atanmış yolcu (aktarım yayınlanınca 0'a iner) */
  passengers: number;
  status: string;
}

export interface OptimizationRecommendation {
  run: OptimizationRunRow;
  scenario: OptimizationScenarioItem | null;
  headline: string;
  reasons: string[];
  cautions: string[];
  drafts: ScenarioDraft[];
  removedRoutes: RemovedRouteState[];
  /** önerilen kalkış saatleri tarifeden ayrıca güncellenir */
  scheduleNotes: string[];
}

export interface ScenarioApproveResult {
  scenarioId: string;
  label: string;
  drafts: ScenarioDraft[];
  removed: string[];
}

export interface ScenarioPublishResult {
  published: { code: string; version: number; moved: number; notified: number }[];
  /** yayınlanamayan ilk hat (devam eden sefer vb.); kalanlar taslakta bekler */
  failed: { code: string; message: string } | null;
}

/** Optimizasyon olayları (ops:<firma> kanalı): `optimization.progress` ve `optimization.done`. */
export interface OptimizationEvent {
  type: 'optimization.progress' | 'optimization.done';
  runId: string;
  progress: number;
  status: OptimizationRunStatus;
}

// ───────── rota versiyonları (C16) ve karşılaştırma (C17) ─────────

export interface RouteVersionRow extends RouteVersionItem {
  /** optimizasyon onayından doğan taslak/versiyon (senaryo harfi) */
  fromScenario: string | null;
}

export interface RouteVersionsPage {
  route: { id: string; code: string; name: string; institution: string; status: string };
  versions: RouteVersionRow[];
  publishedId: string | null;
  draftId: string | null;
}

export const VERSION_STOP_CHANGES = ['ADDED', 'REMOVED', 'MOVED', 'RENAMED', 'RETIMED', 'UNCHANGED'] as const;
export type VersionStopChange = (typeof VERSION_STOP_CHANGES)[number];
export const VERSION_STOP_CHANGE_LABELS: Record<VersionStopChange, string> = {
  ADDED: 'Eklendi',
  REMOVED: 'Kaldırıldı',
  MOVED: 'Taşındı',
  RENAMED: 'Adı değişti',
  RETIMED: 'Saati değişti',
  UNCHANGED: 'Değişmedi',
};

export interface VersionStopSide {
  order: number;
  name: string;
  lat: number;
  lng: number;
  offsetS: number;
}

export interface VersionStopDiff {
  key: string;
  change: VersionStopChange;
  /** eski versiyondaki durak (eklenende yok) */
  a: VersionStopSide | null;
  /** yeni versiyondaki durak (kaldırılanda yok) */
  b: VersionStopSide | null;
  movedM: number | null;
  /** planlanan saat farkı (sn, + geç) */
  offsetDeltaS: number | null;
}

export const VersionCompareQuery = z.object({
  a: Id.optional(),
  b: Id.optional(),
});
export type VersionCompareQuery = z.infer<typeof VersionCompareQuery>;

export interface RouteVersionCompare {
  route: { id: string; code: string; name: string };
  versions: RouteVersionItem[];
  a: RouteVersionDetail;
  b: RouteVersionDetail;
  stops: VersionStopDiff[];
  counts: Record<VersionStopChange, number>;
  distanceDeltaM: number;
  durationDeltaS: number;
}
